// @ts-nocheck


import React, { useEffect, useState, useContext, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import { listenToHostShortstays, upsertShortstay } from "../../../redux/shortstaysSlice";
import { listenToListingAvailabilityByHours, listenToListingReservations } from "../../../redux/shortstayReservationSlice";
import {
  format, addMonths, subMonths, startOfMonth, endOfMonth,
  startOfWeek, endOfWeek, addDays, isBefore, isSameDay, isToday,
} from "date-fns";
import { useNavigate } from "react-router-dom";
import { CurrencyContext } from "../../../GLOBAL/CurrencyContext";

// ── Cloud Function endpoints ──────────────────────────────────────────────────
const SYNC_CALENDARS_URL       = "https://europe-west3-alreluxe.cloudfunctions.net/syncCalendars";
const TOGGLE_CALENDAR_SYNC_URL = "https://europe-west3-alreluxe.cloudfunctions.net/toggleCalendarSync";

// ── Palette ───────────────────────────────────────────────────────────────────
const C = {
  blue:    "#1d4ed8",
  blueMid: "#3b82f6",
  blueSoft:"#bfdbfe",
  white:   "#FFFFFF",
  red:     "#e53e3e",
  green:   "#16a34a",
  muted:   "#94a3b8",
  border:  "#e2e8f0",
  text:    "#0f172a",
  textSub: "#64748b",
  bg:      "#f8fafc",
  gold:    "#F5A623",
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const getRateLabel = (rateType?: string) => {
  switch (rateType) {
    case "perNightPerPerson": return "person / night";
    case "perHourPerPerson":  return "person / hour";
    case "perHour":           return "hour";
    default:                  return "night";
  }
};

const getDiscountMultiplier = (listing: any) => {
  const d = listing?.discounts;
  if (!d) return 1;
  if (d.is80) return 0.2;
  if (d.is50) return 0.5;
  if (d.is25) return 0.75;
  if (d.is10) return 0.9;
  if (d.is5)  return 0.95;
  return 1;
};

const toDateStr = (d: Date) => d.toLocaleDateString("en-CA");

// ── Glass toggle switch ───────────────────────────────────────────────────────
const ToggleSwitch = ({ checked, disabled, onChange }: { checked: boolean; disabled?: boolean; onChange: () => void }) => (
  <div
    onClick={() => !disabled && onChange()}
    role="switch"
    aria-checked={checked}
    style={{
      width: 52, height: 28, borderRadius: 999,
      cursor: disabled ? "not-allowed" : "pointer",
      background: checked
        ? "linear-gradient(135deg, #16a34a, #22c55e)"
        : "linear-gradient(135deg, #cbd5e1, #94a3b8)",
      position: "relative",
      transition: "background .25s",
      flexShrink: 0,
      opacity: disabled ? 0.5 : 1,
      boxShadow: checked
        ? "0 0 12px rgba(34,197,94,0.4), inset 0 1px 0 rgba(255,255,255,0.25)"
        : "inset 0 1px 0 rgba(255,255,255,0.15)",
    }}
  >
    <div style={{
      width: 22, height: 22, borderRadius: "50%",
      background: "linear-gradient(145deg, #ffffff, #f1f5f9)",
      position: "absolute", top: 3, left: checked ? 27 : 3,
      transition: "left .25s cubic-bezier(.34,1.56,.64,1)",
      boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
    }} />
  </div>
);

// ── Global styles injected once ───────────────────────────────────────────────
const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

  * { box-sizing: border-box; }

  .cal-day {
    position: relative;
    aspect-ratio: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 500;
    border-radius: 6px;
    cursor: pointer;
    user-select: none;
    transition: all 0.15s ease;
    color: #0f172a;
  }

  .cal-day:hover:not(.cal-day--disabled):not(.cal-day--past):not(.cal-day--blocked):not(.cal-day--reserved) {
    background: rgba(29,78,216,0.08);
    transform: scale(1.08);
    box-shadow:
      0 0 0 1px rgba(29,78,216,0.2),
      0 4px 12px rgba(29,78,216,0.15),
      inset 0 1px 0 rgba(255,255,255,0.8);
    color: #1d4ed8;
    font-weight: 600;
    z-index: 2;
  }

  .cal-day--other-month {
    opacity: 0.18;
    pointer-events: none;
  }

  .cal-day--past {
    color: #cbd5e1;
    cursor: default;
  }

  .cal-day--past:hover {
    transform: none;
    background: transparent;
    box-shadow: none;
  }

  .cal-day--today {
    font-weight: 700;
  }

  .cal-day--today::after {
    content: '';
    position: absolute;
    bottom: 3px;
    left: 50%;
    transform: translateX(-50%);
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: #1d4ed8;
  }

  .cal-day--blocked {
    background: linear-gradient(135deg, #ef4444, #b91c1c) !important;
    color: #fff !important;
    cursor: not-allowed;
    box-shadow: 0 2px 8px rgba(239,68,68,0.35), inset 0 1px 0 rgba(255,255,255,0.2) !important;
  }

  .cal-day--reserved {
    background: linear-gradient(135deg, #22c55e, #15803d) !important;
    color: #fff !important;
    cursor: not-allowed;
    box-shadow: 0 2px 8px rgba(34,197,94,0.35), inset 0 1px 0 rgba(255,255,255,0.2) !important;
  }

  .cal-day--sel-single,
  .cal-day--sel-start,
  .cal-day--sel-end {
    background: linear-gradient(135deg, #2563eb, #1d4ed8) !important;
    color: #fff !important;
    box-shadow: 0 0 0 2px rgba(37,99,235,0.25), 0 4px 14px rgba(37,99,235,0.4), inset 0 1px 0 rgba(255,255,255,0.25) !important;
    font-weight: 700;
    z-index: 2;
  }

  .cal-day--sel-start { border-radius: 6px 0 0 6px !important; }
  .cal-day--sel-end   { border-radius: 0 6px 6px 0 !important; }

  .cal-day--sel-mid {
    background: #dbeafe !important;
    color: #1e3a8a !important;
    border-radius: 0 !important;
    font-weight: 500;
  }

  .cal-day--disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .cal-day--disabled:hover {
    transform: none !important;
    box-shadow: none !important;
  }

  .month-block-enter {
    animation: monthFadeIn 0.3s ease forwards;
  }

  @keyframes monthFadeIn {
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  @keyframes slideUp {
    from { transform: translateY(100%); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
  }

  @keyframes fadeScaleIn {
    from { opacity: 0; transform: scale(0.96); }
    to   { opacity: 1; transform: scale(1); }
  }

  ::-webkit-scrollbar { display: none; }
`;

// ── Single month grid ─────────────────────────────────────────────────────────
interface MonthGridProps {
  monthDate:           Date;
  today:               Date;
  blockedDates:        string[];
  reservedDates:       string[];
  selStart:            Date | null;
  selEnd:              Date | null;
  hourDates:           string[];
  isPerHour:           boolean;
  hourAvailabilityMap: any;
  selectedListingId:   string | null;
  onDateClick:         (date: Date) => void;
}

const MonthGrid: React.FC<MonthGridProps> = ({
  monthDate, today, blockedDates, reservedDates,
  selStart, selEnd, hourDates, isPerHour,
  hourAvailabilityMap, selectedListingId, onDateClick,
}) => {
  const todayStr  = toDateStr(today);
  const monthStart = startOfMonth(monthDate);
  const gridStart  = startOfWeek(monthStart);
  const gridEnd    = endOfWeek(endOfMonth(monthStart));

  const days: React.ReactNode[] = [];
  let cursor = gridStart;

  while (cursor <= gridEnd) {
    const day     = new Date(cursor);
    const dateStr = toDateStr(day);
    const inMonth = day.getMonth() === monthDate.getMonth();

    const isPast     = isBefore(day, new Date(todayStr));
    const isBlocked  = blockedDates.includes(dateStr);
    const isReserved = !isPerHour && reservedDates.includes(dateStr);
    const isHourSel  = isPerHour && hourDates.includes(dateStr);

    const hourAvail    = hourAvailabilityMap?.listingId === selectedListingId && hourAvailabilityMap?.date === dateStr ? hourAvailabilityMap : null;
    const blockedCount = hourAvail?.disabledHours?.length || 0;
    const isFullBlocked = isPerHour && blockedCount === 24;
    const isPartBlocked = isPerHour && blockedCount > 0 && blockedCount < 24;

    const isStart  = selStart && isSameDay(day, selStart);
    const isEnd    = selEnd   && isSameDay(day, selEnd);
    const isSingle = selStart && !selEnd && isSameDay(day, selStart);
    const inRange  = selStart && selEnd && day > selStart && day < selEnd;

    const isDisabled = isPast || isFullBlocked || isReserved || isBlocked;

    let cls = "cal-day";
    if (!inMonth)              cls += " cal-day--other-month";
    else if (isPast)           cls += " cal-day--past";
    else if (isBlocked || isFullBlocked) cls += " cal-day--blocked cal-day--disabled";
    else if (isReserved)       cls += " cal-day--reserved cal-day--disabled";
    else {
      if (isHourSel || isSingle)         cls += " cal-day--sel-single";
      else if (isStart && selEnd)        cls += " cal-day--sel-start";
      else if (isEnd)                    cls += " cal-day--sel-end";
      else if (inRange)                  cls += " cal-day--sel-mid";
    }

    if (dateStr === todayStr && inMonth) cls += " cal-day--today";
    if (isPartBlocked && inMonth)        cls += " cal-day--part-blocked";

    days.push(
      <div
        key={dateStr + (inMonth ? "" : "-out")}
        className={cls}
        onClick={() => !isDisabled && inMonth && onDateClick(day)}
        title={isBlocked ? "Blocked" : isReserved ? "Reserved" : undefined}
      >
        {format(day, "d")}
        {isPartBlocked && inMonth && (
          <div style={{
            position: "absolute", bottom: 3, left: "50%",
            transform: "translateX(-50%)",
            width: 4, height: 4, borderRadius: "50%",
            background: "linear-gradient(135deg, #f59e0b, #d97706)",
            boxShadow: "0 0 4px rgba(245,158,11,0.6)",
          }} />
        )}
      </div>
    );

    cursor = addDays(cursor, 1);
  }

  return (
    <div className="month-block-enter" style={{ width: "100%" }}>
      {/* Month title */}
      <div style={{
        fontSize: 14, fontWeight: 700,
        color: C.text,
        marginBottom: 14,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
      }}>
        {format(monthDate, "MMMM yyyy")}
      </div>

      {/* Weekday headers */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", marginBottom: 4 }}>
        {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => (
          <div key={d} style={{
            textAlign: "center",
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: C.muted,
            padding: "2px 0 6px",
          }}>{d}</div>
        ))}
      </div>

      {/* Day cells */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 1 }}>
        {days}
      </div>
    </div>
  );
};

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
const ShortAccomodationCalendar: React.FC = () => {
  const dispatch  = useDispatch<AppDispatch>();
  const navigate  = useNavigate();
  const { rate, symbol } = useContext(CurrencyContext);

  const { listings, loading } = useSelector((s: RootState) => s.shortstays);
  const reservations       = useSelector((s: RootState) => s.shortstayReservations.listingReservations);
  const hourAvailabilityMap = useSelector((s: RootState) => s.shortstays.hourAvailability);
  const currentUserId      = useSelector((s: RootState) => s.auth?.user?.uid);

  const [selectedListingId, setSelectedListingId] = useState<string | null>(null);
  const [expanded,          setExpanded]          = useState(false);
  const [selStart,          setSelStart]          = useState<Date | null>(null);
  const [selEnd,            setSelEnd]            = useState<Date | null>(null);
  const [toggleMode,        setToggleMode]        = useState<"block"|"unblock">("block");
  const [showModal,         setShowModal]         = useState(false);
  const [selectedHours,     setSelectedHours]     = useState<number[]>([]);
  const [hourDates,         setHourDates]         = useState<string[]>([]);
  const [isMobile,          setIsMobile]          = useState(typeof window !== "undefined" && window.innerWidth < 768);

  // Infinite scroll state
  const [monthsCount,  setMonthsCount]  = useState(6);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Navigation (for past months)
  const [monthOffset, setMonthOffset] = useState(0); // 0 = current month is first visible

  // Calendar sync
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [feedUrl,     setFeedUrl]     = useState<string | null>(null);
  const [syncError,   setSyncError]   = useState<string | null>(null);
  const [copied,      setCopied]      = useState(false);

  const selectedListing = listings.find(l => l.listingId === selectedListingId);
  const today           = new Date(); today.setHours(0, 0, 0, 0);
  const now             = new Date();
  const todayStr        = toDateStr(now);
  const currentHour     = now.getHours();
  const isPerHour       = selectedListing?.rateType?.includes("perHour");

  // Months to show: starts at (today + monthOffset), shows monthsCount months
  const visibleMonths: Date[] = Array.from({ length: monthsCount }, (_, i) =>
    addMonths(startOfMonth(today), monthOffset + i)
  );

  // Can go back?
  const canGoBack = monthOffset > 0;

  // ── Responsive ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const r = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", r);
    return () => window.removeEventListener("resize", r);
  }, []);

  // ── Infinite scroll via IntersectionObserver ──────────────────────────────
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        setMonthsCount(prev => prev + (isMobile ? 3 : 6));
      }
    }, { rootMargin: "300px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [isMobile]);

  // ── Data ──────────────────────────────────────────────────────────────────────
  useEffect(() => { dispatch(listenToHostShortstays()); }, [dispatch]);

  useEffect(() => {
    if (listings.length) setSelectedListingId(listings[listings.length - 1]?.listingId);
  }, [listings]);

  useEffect(() => {
    if (!selectedListingId) return;
    const unsub = dispatch(listenToListingReservations(selectedListingId));
    return () => { if (typeof unsub === "function") unsub(); };
  }, [dispatch, selectedListingId]);

  useEffect(() => {
    if (!isPerHour || !selectedListingId || hourDates.length === 0) return;
    const unsubs = hourDates.map(date =>
      dispatch(listenToListingAvailabilityByHours({ listingId: selectedListingId, date }))
    );
    return () => { unsubs.forEach(u => { if (typeof u === "function") u(); }); };
  }, [dispatch, isPerHour, selectedListingId, hourDates]);

  // ── Sync state ────────────────────────────────────────────────────────────────
  useEffect(() => {
    setSyncEnabled(!!selectedListing?.calendarSyncEnabled);
    setSyncError(null);
    setCopied(false);
    if (selectedListing?.calendarExportToken && selectedListing?.calendarSyncEnabled) {
      setFeedUrl(`https://europe-west3-alreluxe.cloudfunctions.net/exportCalendarFeed/${selectedListingId}/${selectedListing.calendarExportToken}.ics`);
    } else {
      setFeedUrl(null);
    }
  }, [selectedListingId, selectedListing?.calendarSyncEnabled, selectedListing?.calendarExportToken]);

  // ── Reserved dates ────────────────────────────────────────────────────────────
  const reservedDateStrings = reservations
    .filter(r => !r.isCancelled && !r.isRefunded && r.checkIn && r.checkOut)
    .flatMap(r => {
      const dates: string[] = [];
      let cursor = new Date(r.checkIn);
      const co   = new Date(r.checkOut);
      while (cursor < co) { dates.push(toDateStr(cursor)); cursor.setDate(cursor.getDate() + 1); }
      return dates;
    });

  // ── Date click ────────────────────────────────────────────────────────────────
  const handleDateClick = useCallback((date: Date) => {
    if (date < today) return;
    const dateStr = toDateStr(date);

    if (isPerHour) {
      setSelStart(null); setSelEnd(null);
      setHourDates(prev => prev.includes(dateStr) ? prev.filter(d => d !== dateStr) : [...prev, dateStr]);
      const existing = selectedListing?.unavailableHours?.find((h: any) => h.date === dateStr);
      setSelectedHours(existing?.hours || []);
      setShowModal(true);
      return;
    }

    if (!selStart || (selStart && selEnd)) {
      setSelStart(date); setSelEnd(null); setShowModal(false);
    } else if (selStart && !selEnd) {
      if (date > selStart) { setSelEnd(date); setShowModal(true); }
      else { setSelStart(date); }
    }
  }, [selStart, selEnd, today, isPerHour, selectedListing]);

  // ── Save ──────────────────────────────────────────────────────────────────────
  const handleSaveDates = async () => {
    if (!selectedListingId) return;
    const updates: any = {};

    if (selStart && selEnd) {
      const range: string[] = [];
      const cursor = new Date(selStart);
      while (cursor <= selEnd) { range.push(toDateStr(cursor)); cursor.setDate(cursor.getDate() + 1); }
      updates.unavailableDates = toggleMode === "block"
        ? Array.from(new Set([...(selectedListing?.unavailableDates || []), ...range]))
        : (selectedListing?.unavailableDates || []).filter((d: string) => !range.includes(d));
    }

    if (isPerHour && hourDates.length) {
      const existing = selectedListing?.unavailableHours || [];
      let updated = [...existing];
      hourDates.forEach(date => {
        const idx   = updated.findIndex(h => h.date === date);
        const entry = updated[idx];
        if (toggleMode === "block") {
          const merged = Array.from(new Set([...(entry?.hours || []), ...selectedHours]));
          idx >= 0 ? (updated[idx] = { date, hours: merged }) : updated.push({ date, hours: selectedHours });
        }
        if (toggleMode === "unblock") {
          if (selectedHours.length === 0) {
            if (idx >= 0) updated.splice(idx, 1);
            updates.unavailableDates = (selectedListing?.unavailableDates || []).filter((d: string) => d !== date);
            return;
          }
          if (entry) {
            const remaining = entry.hours.filter((h: number) => !selectedHours.includes(h));
            remaining.length === 0 ? updated.splice(idx, 1) : (updated[idx] = { date, hours: remaining });
          }
        }
      });
      updates.unavailableHours = updated;
    }

    await dispatch(upsertShortstay({ listingId: selectedListingId, data: updates }));
    setSelStart(null); setSelEnd(null); setHourDates([]); setSelectedHours([]); setShowModal(false);
  };

  // ── Calendar sync ─────────────────────────────────────────────────────────────
  const handleToggleSync = async () => {
    if (!selectedListingId || !currentUserId || syncLoading) return;
    const next = !syncEnabled;
    setSyncLoading(true); setSyncError(null);
    try {
      if (next && !selectedListing?.calendarExportToken) {
        const res  = await fetch(SYNC_CALENDARS_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ listingId: selectedListingId, hostId: currentUserId }) });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to generate sync link.");
        setFeedUrl(data.feedUrl); setSyncEnabled(true);
        await dispatch(upsertShortstay({ listingId: selectedListingId, data: { calendarSyncEnabled: true } }));
      } else {
        const res  = await fetch(TOGGLE_CALENDAR_SYNC_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ listingId: selectedListingId, hostId: currentUserId, enabled: next }) });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to update sync status.");
        setSyncEnabled(data.calendarSyncEnabled);
        await dispatch(upsertShortstay({ listingId: selectedListingId, data: { calendarSyncEnabled: data.calendarSyncEnabled } }));
      }
    } catch (err: any) {
      setSyncError(err.message || "Something went wrong.");
    } finally {
      setSyncLoading(false);
    }
  };

  const handleCopyFeedUrl = async () => {
    if (!feedUrl) return;
    try { await navigator.clipboard.writeText(feedUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { setSyncError("Couldn't copy — please select and copy manually."); }
  };

  // ── Selected range ────────────────────────────────────────────────────────────
  const selectedDateRange: string[] = (() => {
    if (!selStart) return [];
    if (!selEnd)   return [toDateStr(selStart)];
    const range: string[] = [];
    const cursor = new Date(selStart);
    while (cursor <= selEnd) { range.push(toDateStr(cursor)); cursor.setDate(cursor.getDate() + 1); }
    return range;
  })();

  const unavailableCount = selectedListing?.unavailableDates?.length ?? 0;
  const reservedCount    = reservedDateStrings.length;

  // ── Modal content ─────────────────────────────────────────────────────────────
  const ModalContent = () => (
    <>
      {isMobile && (
        <div style={{ width: 40, height: 4, borderRadius: 999, background: C.border, alignSelf: "center", marginBottom: 20 }} />
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <span style={{ fontSize: 17, fontWeight: 700, color: C.text }}>
          {toggleMode === "block" ? "Block dates" : "Unblock dates"}
        </span>
        <button
          onClick={() => setShowModal(false)}
          style={{ width: 32, height: 32, borderRadius: "50%", background: C.bg, border: `1px solid ${C.border}`, cursor: "pointer", display: "grid", placeItems: "center", fontSize: 16, color: C.textSub }}
        >×</button>
      </div>

      {/* Block / Unblock toggle */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 20 }}>
        {(["block", "unblock"] as const).map(mode => (
          <button
            key={mode}
            onClick={() => setToggleMode(mode)}
            style={{
              padding: "11px 0", borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: "pointer",
              border: "none",
              background: toggleMode === mode
                ? mode === "block"
                  ? "linear-gradient(135deg, #ef4444, #b91c1c)"
                  : "linear-gradient(135deg, #22c55e, #15803d)"
                : C.bg,
              color: toggleMode === mode ? "#fff" : C.textSub,
              boxShadow: toggleMode === mode
                ? mode === "block"
                  ? "0 4px 14px rgba(239,68,68,0.35), inset 0 1px 0 rgba(255,255,255,0.2)"
                  : "0 4px 14px rgba(34,197,94,0.35), inset 0 1px 0 rgba(255,255,255,0.2)"
                : "none",
              transition: "all .2s",
            }}
          >
            {mode === "block" ? "Block" : "Unblock"}
          </button>
        ))}
      </div>

      {!isPerHour && selStart && selEnd && (
        <div style={{ background: "#eff6ff", borderRadius: 10, padding: "10px 14px", marginBottom: 18, fontSize: 13, fontWeight: 500, color: C.blue }}>
          {format(selStart, "MMM d")} → {format(selEnd, "MMM d, yyyy")} · {selectedDateRange.length} night{selectedDateRange.length !== 1 ? "s" : ""}
        </div>
      )}

      {/* Hour grid */}
      {isPerHour && hourDates.length > 0 && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Select hours · {hourDates.length} day{hourDates.length !== 1 ? "s" : ""}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 6, marginBottom: 18 }}>
            {Array.from({ length: 24 }, (_, h) => {
              const isToday_   = hourDates[0] === todayStr;
              const isPastHour = isToday_ && h < currentHour;
              const isRes      = hourAvailabilityMap?.listingId === selectedListingId && hourAvailabilityMap?.date === hourDates[0] && hourAvailabilityMap?.disabledHours?.includes(h);
              const isDis      = isPastHour || isRes;
              const isChosen   = selectedHours.includes(h);
              return (
                <button
                  key={h}
                  disabled={isDis}
                  onClick={() => !isDis && setSelectedHours(prev => prev.includes(h) ? prev.filter(x => x !== h) : [...prev, h])}
                  style={{
                    padding: "8px 0", borderRadius: 8, fontSize: 12, fontWeight: 600,
                    cursor: isDis ? "not-allowed" : "pointer",
                    border: isChosen ? `2px solid ${C.blue}` : `1px solid ${C.border}`,
                    background: isDis ? C.bg : isChosen ? "linear-gradient(135deg, #2563eb, #1d4ed8)" : "#fff",
                    color: isChosen ? "#fff" : isDis ? C.muted : C.text,
                    opacity: isDis ? 0.4 : 1,
                    transition: "all .15s",
                    boxShadow: isChosen ? "0 2px 8px rgba(37,99,235,0.3)" : "none",
                  }}
                >
                  {h}:00
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <button
          onClick={handleSaveDates}
          style={{
            background: toggleMode === "block"
              ? "linear-gradient(135deg, #ef4444, #b91c1c)"
              : "linear-gradient(135deg, #22c55e, #15803d)",
            color: "#fff", border: "none", borderRadius: 12,
            padding: "14px 0", fontWeight: 700, fontSize: 15, cursor: "pointer",
            boxShadow: toggleMode === "block"
              ? "0 4px 16px rgba(239,68,68,0.4)"
              : "0 4px 16px rgba(34,197,94,0.4)",
          }}
        >
          Save changes
        </button>
        <button
          onClick={() => { setSelStart(null); setSelEnd(null); setShowModal(false); }}
          style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 0", fontWeight: 500, fontSize: 14, color: C.textSub, cursor: "pointer" }}
        >
          Cancel
        </button>
      </div>
    </>
  );

  if (loading) return (
    <div style={{ display: "grid", placeItems: "center", height: "100vh", background: "#fff", color: C.muted, fontWeight: 600, fontFamily: "Inter, system-ui, sans-serif", fontSize: 14 }}>
      Loading listings…
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#fff", fontFamily: "Inter, system-ui, sans-serif", color: C.text }}>

      <style>{GLOBAL_STYLES}</style>

      {/* ── HEADER ─── */}
      <div style={{
        background: "#fff",
        borderBottom: `1px solid ${C.border}`,
        position: "sticky",
        top: 0,
        zIndex: 100,
        backdropFilter: "blur(12px)",
      }}>
        <div style={{ maxWidth: 1080, margin: "0 auto", padding: "0 24px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 60 }}>
            <button
              onClick={() => navigate(-1)}
              style={{ width: 36, height: 36, borderRadius: 8, background: C.bg, border: `1px solid ${C.border}`, cursor: "pointer", display: "grid", placeItems: "center", color: C.text, fontSize: 20, fontWeight: 300 }}
            >‹</button>

            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              {/* Legend chips */}
              {[
                { color: C.blue,  label: "Selected" },
                { color: C.red,   label: "Blocked"  },
                { color: C.green, label: "Reserved" },
              ].map(({ color, label }) => (
                <div key={label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: color }} />
                  <span style={{ fontSize: 12, fontWeight: 500, color: C.textSub }}>{label}</span>
                </div>
              ))}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {/* Blocked / Reserved counts */}
              <span style={{ fontSize: 12, fontWeight: 600, color: C.red }}>{unavailableCount} blocked</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: C.green }}>{reservedCount} reserved</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── BODY ─── */}
      <div style={{ maxWidth: 1080, margin: "0 auto", padding: "32px 24px 80px" }}>

        {/* Listing picker */}
        <div
          onClick={() => setExpanded(!expanded)}
          style={{
            display: "flex", alignItems: "center", gap: 14,
            padding: "14px 16px", borderRadius: 14,
            border: `1px solid ${C.border}`,
            cursor: "pointer", marginBottom: 24,
            transition: "border-color .15s",
            background: "#fff",
          }}
        >
          {selectedListing?.photos?.[0]
            ? <img src={selectedListing.photos[0]} alt="" style={{ width: 56, height: 56, borderRadius: 10, objectFit: "cover", flexShrink: 0 }} />
            : <div style={{ width: 56, height: 56, borderRadius: 10, background: C.bg, display: "grid", placeItems: "center", flexShrink: 0, color: C.muted, fontSize: 20 }}>🏠</div>
          }
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {selectedListing?.title || "Untitled"}
            </div>
            {selectedListing && Number.isFinite(selectedListing.rate)
              ? <div style={{ fontSize: 13, color: C.textSub }}>
                  {symbol}{(selectedListing.rate * getDiscountMultiplier(selectedListing) * rate).toLocaleString()} / {getRateLabel(selectedListing.rateType)}
                </div>
              : <div style={{ fontSize: 13, color: C.muted }}>Price N/A</div>
            }
          </div>
          <span style={{ color: C.muted, fontSize: 18, display: "inline-block", transform: expanded ? "rotate(180deg)" : "rotate(0)", transition: "transform .25s" }}>▾</span>
        </div>

        {/* Thumb grid */}
        {expanded && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 24 }}>
            {listings.map(l => (
              <div
                key={l.listingId}
                onClick={() => { setSelectedListingId(l.listingId); setExpanded(false); }}
                style={{ borderRadius: 10, overflow: "hidden", border: l.listingId === selectedListingId ? `2px solid ${C.blue}` : `1px solid ${C.border}`, cursor: "pointer" }}
              >
                <img src={l.photos?.[0] || "https://via.placeholder.com/150"} alt="" style={{ width: "100%", height: 72, objectFit: "cover" }} />
                <div style={{ padding: "6px 8px", fontSize: 11, fontWeight: 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {l.title || "Untitled"}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Calendar Sync */}
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 14, padding: 16, marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 2 }}>Calendar sync</div>
              <div style={{ fontSize: 12, color: C.textSub }}>
                {syncLoading ? "Updating…" : syncEnabled ? "On — Airbnb / VRBO / Booking.com can read this calendar" : "Off — export link is paused"}
              </div>
            </div>
            <ToggleSwitch checked={syncEnabled} disabled={syncLoading} onChange={handleToggleSync} />
          </div>
          {syncError && <div style={{ marginTop: 10, fontSize: 12, color: C.red }}>{syncError}</div>}
          {syncEnabled && feedUrl && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, marginBottom: 8 }}>
                Paste into Airbnb / VRBO / Booking.com's "Import Calendar" field
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <input readOnly value={feedUrl} onFocus={e => e.target.select()}
                  style={{ flex: 1, fontSize: 11, padding: "8px 10px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.bg, color: C.text, minWidth: 0 }}
                />
                <button
                  onClick={handleCopyFeedUrl}
                  style={{ padding: "8px 14px", borderRadius: 8, border: "none", background: copied ? C.green : C.blue, color: "#fff", fontWeight: 600, fontSize: 12, cursor: "pointer", flexShrink: 0 }}
                >
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Month navigation — back button */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
          <button
            onClick={() => canGoBack && setMonthOffset(o => o - 1)}
            disabled={!canGoBack}
            style={{
              padding: "8px 16px", borderRadius: 8,
              border: `1px solid ${C.border}`,
              background: canGoBack ? "#fff" : C.bg,
              color: canGoBack ? C.text : C.muted,
              fontWeight: 500, fontSize: 13, cursor: canGoBack ? "pointer" : "not-allowed",
              opacity: canGoBack ? 1 : 0.4,
              transition: "all .15s",
            }}
          >
            ← Previous month
          </button>
          <span style={{ fontSize: 13, color: C.textSub, fontWeight: 500 }}>
            {format(visibleMonths[0], "MMMM yyyy")} – {format(visibleMonths[visibleMonths.length - 1], "MMMM yyyy")}
          </span>
          <div style={{ width: 140 }} />
        </div>

        {/* Action bar (shows when a range is selected) */}
        {(selectedDateRange.length > 0 || hourDates.length > 0) && (
          <div style={{ display: "flex", gap: 10, marginBottom: 28 }}>
            {(["block", "unblock"] as const).map(mode => (
              <button
                key={mode}
                onClick={() => { setToggleMode(mode); setShowModal(true); }}
                style={{
                  flex: 1, padding: "11px 0", borderRadius: 10,
                  fontWeight: 600, fontSize: 13, cursor: "pointer",
                  border: `1.5px solid ${mode === "block" ? C.red : C.green}`,
                  background: "transparent",
                  color: mode === "block" ? C.red : C.green,
                  transition: "all .15s",
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLButtonElement).style.background = mode === "block" ? C.red : C.green;
                  (e.currentTarget as HTMLButtonElement).style.color = "#fff";
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLButtonElement).style.background = "transparent";
                  (e.currentTarget as HTMLButtonElement).style.color = mode === "block" ? C.red : C.green;
                }}
              >
                {mode === "block" ? "Block" : "Unblock"} {selectedDateRange.length} date{selectedDateRange.length !== 1 ? "s" : ""}
              </button>
            ))}
            <button
              onClick={() => { setSelStart(null); setSelEnd(null); }}
              style={{ padding: "11px 18px", borderRadius: 10, border: `1px solid ${C.border}`, background: "transparent", color: C.textSub, fontWeight: 500, fontSize: 13, cursor: "pointer" }}
            >
              Clear
            </button>
          </div>
        )}

        {/* ── CALENDAR GRID ── */}
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
          gap: isMobile ? 40 : "48px 40px",
        }}>
          {visibleMonths.map((monthDate, i) => (
            <MonthGrid
              key={`${monthDate.getFullYear()}-${monthDate.getMonth()}`}
              monthDate={monthDate}
              today={today}
              blockedDates={selectedListing?.unavailableDates || []}
              reservedDates={reservedDateStrings}
              selStart={selStart}
              selEnd={selEnd}
              hourDates={hourDates}
              isPerHour={!!isPerHour}
              hourAvailabilityMap={hourAvailabilityMap}
              selectedListingId={selectedListingId}
              onDateClick={handleDateClick}
            />
          ))}
        </div>

        {/* IntersectionObserver sentinel */}
        <div ref={sentinelRef} style={{ height: 1, width: "100%" }} />
      </div>

      {/* ── MODAL / BOTTOM SHEET ── */}
      {showModal && (
        <div
          onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: isMobile ? "flex-end" : "center",
            justifyContent: "center",
            zIndex: 300,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: isMobile ? "20px 20px 0 0" : 20,
              padding: 28,
              width: "100%",
              maxWidth: 420,
              maxHeight: "85vh",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              animation: isMobile ? "slideUp .3s ease" : "fadeScaleIn .2s ease",
            }}
          >
            <ModalContent />
          </div>
        </div>
      )}
    </div>
  );
};

export default ShortAccomodationCalendar;